import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusCard } from '@/components/ui/card';
import { RefreshCcw } from 'lucide-react';
import type { Result } from '@/lib/http/HttpClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { useState, useEffect } from 'react';
import { ToolStateBadge } from '@/components/shared/ToolStateBadge';
import { formatDate } from '../utils/toolViewHelpers';

export interface ToolTableRow {
  id: string;
  reference: string;
  family: string;
  health: string; // Kept for compatibility but not displayed
  toolState: string;
  lastTraceDate: string | null;
  traceCount: number;
  onNavigate: () => void;
}

interface ToolsTableProps {
  isLoading: boolean;
  apiResult: Result<AllToolsResponse> | null;
  tableRows: ToolTableRow[];
  totalReferences: number;
  onRetry: () => void;
}

export function ToolsTable({
  isLoading,
  apiResult,
  tableRows,
  totalReferences,
  onRetry,
}: ToolsTableProps) {
  const { t, language } = useTranslation();
  const { resolve } = useReferenceAlias();
  const [visibleCount, setVisibleCount] = useState(10);

  // Reset visible count when tableRows changes (filters applied)
  useEffect(() => {
    setVisibleCount(10);
  }, [tableRows.length]);

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="px-2 pb-2 w-[35%]">
                  <Skeleton className="h-4 w-20" />
                </th>
                <th className="px-2 pb-2 w-[25%]">
                  <Skeleton className="h-4 w-20" />
                </th>
                <th className="px-2 pb-2 w-[25%]">
                  <Skeleton className="h-4 w-20" />
                </th>
                <th className="px-2 pb-2 w-[15%]">
                  <Skeleton className="h-4 w-20" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td className="px-2 py-2">
                    <Skeleton className="h-5 w-32" />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton className="h-5 w-24" />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton className="h-4 w-20" />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (apiResult && !apiResult.ok) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <StatusCard type="error" message={apiResult.message || t('toolView.list.table.error')} />
        <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCcw className="size-4" aria-hidden />
          {t('toolView.list.table.retry')}
        </Button>
      </div>
    );
  }

  const displayedRows = tableRows.slice(0, visibleCount);
  const hasMore = visibleCount < tableRows.length;
  const hasVisibleTableRows = displayedRows.length > 0;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  return (
    <div className="w-full space-y-4">
      {tableRows.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('toolView.list.pagination.showing', {
            count: displayedRows.length,
            total: tableRows.length,
          })}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="eyebrow px-2 pb-2 text-left w-[35%]">
                {t('toolView.list.table.headers.tool')}
              </th>
              <th className="eyebrow px-2 pb-2 text-left w-[25%]">
                {t('toolView.list.table.headers.reference')}
              </th>
              <th className="eyebrow px-2 pb-2 text-left w-[25%]">
                {t('toolView.list.table.headers.lastTrace')}
              </th>
              <th className="eyebrow px-2 pb-2 text-left w-[15%]">
                {t('toolView.list.table.headers.state')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {hasVisibleTableRows ? (
              displayedRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={row.onNavigate}
                  className="cursor-pointer text-foreground transition hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  tabIndex={0}
                  role="button"
                  aria-label={t('toolView.list.table.openTool', { toolId: row.id })}
                  title={t('toolView.list.table.openTool', { toolId: row.id })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      row.onNavigate();
                    }
                  }}
                >
                  <td className="px-2 py-2 font-semibold truncate">{row.id}</td>
                  <td className="px-2 py-2 text-muted-foreground truncate">
                    {(() => {
                      const resolved = resolve(row.reference);
                      return (
                        <>
                          <span>{resolved.label}</span>
                          {resolved.hasAlias && (
                            <span className="block font-mono text-xs text-muted-foreground/70">
                              {resolved.canonical}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 text-xs text-muted-foreground truncate">
                    {row.traceCount > 0
                      ? `${row.traceCount} ${t(row.traceCount === 1 ? 'toolView.list.traceCount.one' : 'toolView.list.traceCount.many')} · ${formatDate(row.lastTraceDate, language, t, 'toolView.list.table.noTrace')}`
                      : t('toolView.list.table.noTrace')}
                  </td>
                  <td className="px-2 py-2">
                    <ToolStateBadge toolState={row.toolState} size="sm" />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-2 py-6 text-sm text-muted-foreground text-center">
                  {t('toolView.list.table.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {totalReferences > 0
          ? `${t('toolView.list.table.footer.references', { count: totalReferences })} · ${t('toolView.list.table.footer.tools', { count: tableRows.length })}`
          : t('toolView.list.table.footer.noData')}
      </p>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={handleShowMore}>
            {t('toolView.list.pagination.showMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
